import { PaginationProps } from '@/design-system/components/pagination/Pagination';
import { useDebounce } from '@/utils/useDebounce.hook';
import { UserEarnActionListResponseDataItem, useUtxEarnSubscriptionsGetEarnActionList } from '@utx/utx-client';
import { LayoutContext } from 'contexts/LayoutContext';
import { useContext, useMemo, useState } from 'react';
import { useStore } from 'store/store';

const PAGE_SIZE = 8;
const MINUTE = 60 * 1000;
const SECOND = 60;

interface UsePositionsParams {}

export const usePositions = (props: UsePositionsParams) => {
  const store = useStore();
  const layout = useContext(LayoutContext);
  const [paginationProps, setPaginationProps] = useState<PaginationProps>({
    current: 1,
    pageSize: PAGE_SIZE
  });
  const [searchText, setSearchText] = useState<string | undefined>('');
  const debouncedSearchText = useDebounce(searchText);
  const [displayData, setDisplayData] = useState<UserEarnActionListResponseDataItem[]>([]);

  const query = useUtxEarnSubscriptionsGetEarnActionList(
    {
      action: 'SUBSCRIPTION',
      status: 'active',
      current: paginationProps.current,
      pageSize: paginationProps.pageSize,
      asset: debouncedSearchText && debouncedSearchText.toUpperCase()
    },
    {
      axios: {
        headers: {
          Authorization: `Bearer ${store.authToken}`
        }
      },
      query: {
        keepPreviousData: true,
        onSuccess(data) {
          let newDisplayData = [];
          if (paginationProps.current === 1) {
            newDisplayData = data.data?.data || [];
            setDisplayData(newDisplayData);
            return data;
          }

          if (layout.isMobile) {
            newDisplayData = [...displayData, ...(data.data?.data || [])];
          } else {
            newDisplayData = data.data?.data || [];
          }
          setDisplayData(newDisplayData);
          return data;
        },
        refetchInterval: layout.isMobile ? undefined : MINUTE
        // refetchInterval: layout.isMobile ? undefined : SECOND
      }
    }
  );

  const totalPages = useMemo(() => {
    if (!query.data) {
      return 0;
    }
    return Math.ceil(query.data?.data.total / PAGE_SIZE);
  }, [query.data?.data?.total]);

  function handleRefresh() {
    if (paginationProps.current === 1) {
      query.refetch();
      return;
    }
    setPaginationProps({ ...paginationProps, current: 1 });
  }

  function handleNextPage() {
    if (paginationProps.current === totalPages) return;
    setPaginationProps((prev) => ({
      ...prev,
      current: prev.current + 1
    }));
  }

  function handlePrevPage() {
    if (paginationProps.current === 1) return;
    setPaginationProps((prev) => ({
      ...prev,
      current: prev.current - 1
    }));
  }

  function handleSearch(search?: string) {
    setPaginationProps((prev) => ({
      ...prev,
      current: 1
    }));
    setSearchText(search);
  }

  return {
    query,
    data: displayData,
    totalPages,
    pageSize: PAGE_SIZE,
    paginationProps,
    handleNextPage,
    handlePrevPage,
    handleRefresh,
    onFilterChange: handleSearch
  };
};
